import { motion } from "framer-motion";
import { Clock, Disc3, Gamepad2, Glasses, Target, Coffee, Sofa, PartyPopper, Palette, MapPin, BadgeCheck, ScanLine, ShieldCheck, Ghost, CalendarDays, Ticket, ArrowUpRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Eyebrow, Button } from "../components/Bits";
import { IMG } from "../lib/content";

const YAZ_URL = "https://yaz.nead.uk";

const features = [
  { icon: Gamepad2, title: "Gaming" },
  { icon: Glasses, title: "VR" },
  { icon: Target, title: "Pool" },
  { icon: Disc3, title: "Music & DJs" },
  { icon: Coffee, title: "Youth café" },
  { icon: Sofa, title: "Chill-out spaces" },
  { icon: PartyPopper, title: "Events & rave nights" },
  { icon: Palette, title: "Creative activities" },
];

export default function Yaz() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-nead-ink text-white min-h-[90vh] flex items-center pt-[74px]">
        <div className="absolute inset-0">
          <img src={IMG.yaz} alt="YAZ youth cafe in Workington" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-nead-ink via-nead-ink/80 to-nead-ink/50" />
          <div className="absolute inset-0 bg-gradient-to-r from-nead-ink/90 to-transparent" />
        </div>
        <div className="absolute right-[-10%] top-1/4 h-96 w-96 glow-pink pointer-events-none" />
        <div className="relative container-x py-24">
          <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="inline-flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-nead-pink px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-white">
              <MapPin className="h-3.5 w-3.5" /> Workington
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-white backdrop-blur">
              Ages 11 to 19
            </span>
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }} className="mt-6 font-display font-extrabold tracking-tight text-[3.4rem] sm:text-7xl lg:text-[8rem] leading-[0.92]">
            <span className="text-gradient">YAZ</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6 }} className="mt-4 font-display font-bold text-2xl sm:text-3xl text-white">
            Youth Café &amp; Nightspot
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }} className="mt-5 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed">
            A space for young people to hang out, socialise, make friends and have a proper night out.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} className="mt-9 flex flex-wrap gap-3">
            <Button href={YAZ_URL} variant="pink" testId="yaz-visit">Visit yaz.nead.uk</Button>
            <Button href="#halloween" variant="outline-light" testId="yaz-involved">Halloween Rave</Button>
          </motion.div>
        </div>
      </section>

      <div className="h-1.5 w-full grad-pink-cyan" />

      {/* Halloween Rave */}
      <section id="halloween" className="relative overflow-hidden bg-[#0b0608] text-white" data-testid="yaz-halloween">
        <div className="absolute -left-20 top-0 h-80 w-80 rounded-full bg-orange-500/25 blur-3xl pointer-events-none" />
        <div className="absolute right-0 bottom-0 h-72 w-72 rounded-full bg-purple-600/25 blur-3xl pointer-events-none" />
        <div className="relative container-x py-20 md:py-24 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em]">
              <Ghost className="h-3.5 w-3.5" /> Next event
            </div>
            <h2 className="mt-6 font-display font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Halloween <span className="text-orange-400">Rave</span>
            </h2>
            <p className="mt-5 max-w-xl text-lg text-white/75 leading-relaxed">
              YAZ is going spooky. Dress up, bring your mates and dance the night away at our alcohol-free, under-18s Halloween rave.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-[1.75rem] border border-orange-400/30 bg-white/[0.04] p-8 backdrop-blur">
              <div className="flex items-center gap-3 text-white/70"><CalendarDays className="h-5 w-5 text-orange-400" /> Date</div>
              <p className="mt-1 font-display font-extrabold text-3xl" data-testid="yaz-halloween-date">Saturday 24 October</p>
              <div className="mt-6 flex items-center gap-3 text-white/70"><Ticket className="h-5 w-5 text-orange-400" /> Tickets</div>
              <p className="mt-1 font-display font-extrabold text-5xl text-orange-400" data-testid="yaz-halloween-price">£1</p>
              <a
                href={YAZ_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="yaz-halloween-tickets"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-orange-500 px-7 py-4 font-display font-bold text-white hover:bg-white hover:text-[#0b0608] transition-colors"
              >
                Get tickets at yaz.nead.uk <ArrowUpRight className="h-5 w-5" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Intro */}
      <section className="container-x py-24 md:py-28">
        <Reveal>
          <Eyebrow className="text-nead-pink">What is YAZ?</Eyebrow>
          <p className="mt-6 max-w-4xl font-display font-medium text-2xl sm:text-3xl lg:text-[2.4rem] leading-[1.25] tracking-tight text-nead-navy text-balance">
            YAZ is NEAD's dedicated youth café and nightspot in Workington for ages 11 to 19, a safe, welcoming space designed
            around young people and what they actually want.
          </p>
          <p className="mt-6 max-w-2xl text-nead-navy/60 text-lg leading-relaxed">
            Whether you want to grab a drink, play games, chill with friends, have a go on the VR, play pool, listen to
            music or get involved in what's happening, YAZ is somewhere you can come and be yourself.
          </p>
        </Reveal>
      </section>

      {/* How it works / Membership */}
      <section className="bg-nead-navy text-white border-y border-white/10" data-testid="yaz-membership">
        <div className="container-x py-20 md:py-24">
          <Reveal>
            <div className="max-w-2xl">
              <Eyebrow className="text-nead-cyan">How it works</Eyebrow>
              <h2 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl tracking-tight leading-[1.05]">You need a free membership to come in.</h2>
              <p className="mt-4 text-white/70 text-lg leading-relaxed">Membership keeps everyone safe. It is completely free, takes a minute to set up, and once you are a member you simply scan in and out on each visit.</p>
            </div>
          </Reveal>
          <Stagger className="mt-12 grid sm:grid-cols-3 gap-5">
            {[
              { icon: BadgeCheck, title: "Free membership", body: "There is no cost. Every young person aged 11 to 19 is welcome to join." },
              { icon: ScanLine, title: "Scan in and out", body: "Members scan in when they arrive and scan out when they leave, so we always know who is in." },
              { icon: ShieldCheck, title: "Safe and supervised", body: "A supervised, welcoming space designed entirely around young people." },
            ].map((s) => (
              <StaggerItem key={s.title}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-7">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl grad-pink-cyan"><s.icon className="h-6 w-6 text-white" strokeWidth={1.8} /></div>
                  <h3 className="mt-5 font-display font-bold text-xl">{s.title}</h3>
                  <p className="mt-2 text-white/60 leading-relaxed">{s.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal delay={0.1}>
            <div className="mt-8 overflow-hidden rounded-[1.75rem] grad-pink-cyan p-8 md:p-10">
              <div className="flex flex-col md:flex-row md:items-center gap-6 md:justify-between">
                <div className="max-w-2xl">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.14em]"><BadgeCheck className="h-3.5 w-3.5" /> Get your free membership</div>
                  <h3 className="mt-4 font-display font-extrabold text-2xl sm:text-3xl tracking-tight">Sign up online, it takes one minute.</h3>
                  <p className="mt-3 text-white/90 leading-relaxed">
                    Head to the YAZ website at <span className="font-bold">yaz.nead.uk</span> to register for your free membership.
                  </p>
                </div>
                <a
                  href={YAZ_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="yaz-membership-link"
                  className="inline-flex flex-none items-center justify-center gap-2 rounded-full bg-white px-7 py-4 font-display font-bold text-nead-navy hover:bg-nead-navy hover:text-white transition-colors"
                >
                  Join at yaz.nead.uk <ArrowUpRight className="h-5 w-5" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="bg-[#070912] text-white border-y border-white/10">
        <div className="container-x py-24 md:py-28 grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="h-full rounded-[1.75rem] border border-white/10 bg-white/[0.03] p-9">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl grad-pink-cyan"><Clock className="h-6 w-6 text-white" strokeWidth={1.8} /></div>
              <h2 className="mt-6 font-display font-bold text-2xl sm:text-3xl tracking-tight">Opening hours</h2>
              <div className="mt-6 divide-y divide-white/10">
                <div className="flex items-center justify-between py-4"><span className="text-white/70">Friday</span><span className="font-display font-bold text-lg">5:00pm – 10:30pm</span></div>
                <div className="flex items-center justify-between py-4"><span className="text-white/70">Saturday</span><span className="font-display font-bold text-lg">12:00pm – 10:30pm</span></div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative h-full overflow-hidden rounded-[1.75rem] grad-pink-cyan p-9 text-white">
              <div className="absolute right-[-10%] top-[-20%] h-56 w-56 rounded-full bg-white/10 blur-2xl" />
              <div className="relative">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 backdrop-blur"><Disc3 className="h-6 w-6 text-white" strokeWidth={1.8} /></div>
                <h2 className="mt-6 font-display font-bold text-2xl sm:text-3xl tracking-tight">Monthly Rave Nights</h2>
                <p className="mt-4 text-white/90 leading-relaxed">
                  Once a month, YAZ becomes a proper under-18s rave nightspot, with DJs, music, lights, dancing and
                  loads going on.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 font-display font-bold backdrop-blur">
                  <Clock className="h-4 w-4" /> 6:00pm – 11:00pm
                </div>
                <p className="mt-5 text-white/85 text-sm leading-relaxed">
                  Our rave nights are completely alcohol-free, giving young people the chance to have a proper night out
                  in a safe, supervised environment.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What's at YAZ */}
      <section className="container-x py-24 md:py-28">
        <Reveal>
          <Eyebrow className="text-nead-pink">What's at YAZ?</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl tracking-tight text-nead-navy">Loads to do.</h2>
        </Reveal>
        <Stagger className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {features.map((f) => (
            <StaggerItem key={f.title}>
              <div className="group h-full rounded-[1.5rem] bg-white border border-black/[0.07] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-nead-pink/40">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl grad-pink-cyan transition-transform duration-300 group-hover:scale-110"><f.icon className="h-6 w-6 text-white" strokeWidth={1.7} /></div>
                <h3 className="mt-5 font-display font-bold text-lg text-nead-navy">{f.title}</h3>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Made for young people */}
      <section className="relative overflow-hidden bg-nead-navy text-white">
        <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 glow-cyan pointer-events-none" />
        <div className="relative container-x py-24 md:py-32 text-center">
          <Reveal>
            <Eyebrow className="text-nead-cyan justify-center">Made for young people</Eyebrow>
            <h2 className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl mx-auto leading-[1.05]">
              YAZ isn't just a youth project. It's a place for young people to <span className="text-gradient">make their own.</span>
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-white/70 text-lg leading-relaxed">
              We want young people to help shape the music, activities, events and atmosphere, and tell us what they
              want to see happening.
            </p>
            <p className="mt-8 font-display font-bold text-xl text-white">Come down. Hang out. Get involved. Make YAZ yours.</p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <Button href={YAZ_URL} variant="pink" testId="yaz-cta-visit">Visit the YAZ website</Button>
              <Button to="/get-involved" variant="outline-light" testId="yaz-cta-involved">Get involved</Button>
            </div>
            <p className="mt-12 text-sm font-semibold uppercase tracking-[0.2em] text-white/40 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <span>YAZ by NEAD</span><span className="text-nead-pink">•</span><span>Youth Café</span><span className="text-nead-pink">•</span><span>Nightspot</span><span className="text-nead-pink">•</span><span>Community</span><span className="text-nead-pink">•</span><span>Music</span>
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
